import { action, makeObservable, observable } from 'mobx';

import ApiService from '../services/ApiService';

import { ILanguageStore } from './LanguageStore';
import { Invoice } from './models/Invoice';
import { InvoiceFilter } from './models/InvoiceFilter';
import { LoadableArray } from './models/LoadableArray';
import { IPreviousInvoicesFilterStore } from './PreviousInvoicesFilterStore';
import { ISsoStore } from './SsoStore';

export default class PreviousInvoicesStore {
  @observable
  public previousInvoices: LoadableArray<Invoice> = new LoadableArray<Invoice>();
  public previousInvoicesFilter: InvoiceFilter = null;

  private static PREVIOUS_INVOICES_PAGE_SIZE = 50;

  constructor(
    private ssoStore: ISsoStore,
    private languageStore: ILanguageStore,
    private apiService: ApiService,
    private previousInvoicesFilterStore: IPreviousInvoicesFilterStore
  ) {
    makeObservable(this);
  }

  @action
  public async tryLoadPreviousInvoices(): Promise<void> {
    if (!this.previousInvoices.isInitial) {
      await this.previousInvoices.promise;
      return;
    }
    const filter = this.previousInvoicesFilterStore.invoiceFilter;

    await this.loadPreviousInvoices(filter);
  }

  @action
  public async loadPreviousInvoicesWithFilter(filter: InvoiceFilter): Promise<any> {
    if (!this.shouldReloadPreviousInvoices(filter)) {
      return;
    }

    this.loadPreviousInvoices(filter);
  }

  public async loadPreviousInvoices(filter: InvoiceFilter): Promise<any> {
    filter.offset = 0;
    filter.limit = PreviousInvoicesStore.PREVIOUS_INVOICES_PAGE_SIZE;
    this.previousInvoicesFilter = filter;

    this.previousInvoices = new LoadableArray<Invoice>();

    await this.loadMorePreviousInvoices();
  }

  @action
  public async loadMorePreviousInvoices(): Promise<any> {
    if (!this.previousInvoices.isInitial && !this.previousInvoices.isPartiallyLoaded) {
      await this.previousInvoices.promise;
      return;
    }

    const previousInvoicesPromise = this.apiService.getPreviousInvoices(this.ssoStore.customer.customerId, this.previousInvoicesFilter);

    const transform = (result: Invoice[]) => {
      this.previousInvoicesFilter.offset += PreviousInvoicesStore.PREVIOUS_INVOICES_PAGE_SIZE;
      return result;
    };

    const transformedPreviousInvoices = LoadableArray.toLoadableArray<Invoice, Invoice>(previousInvoicesPromise, transform);

    this.previousInvoices.loadMore(transformedPreviousInvoices.promise, (result) => {
      return result.length < PreviousInvoicesStore.PREVIOUS_INVOICES_PAGE_SIZE;
    });

    await transformedPreviousInvoices.promise;
  }

  public shouldReloadPreviousInvoices(newFilter: InvoiceFilter): boolean {
    const previousFilter = this.previousInvoicesFilter;
    if (previousFilter == null) {
      return true;
    }
    if (previousFilter.fromDate !== newFilter.fromDate) {
      return true;
    }
    return previousFilter.toDate !== newFilter.toDate;
  }
}
